import { FunctionsHttpError } from '@supabase/supabase-js';
import { demMainCustomerCode } from './demCustomers';
import { formatSupabaseError } from './formatSupabaseError';
import { normalizeDeliveryPhone } from './intlPhone';
import { getDbSupabase } from './supabase';
import type {
  ConfirmMultiStopDeliveryInput,
  DeliveryArrangement,
  DeliveryArrangementSummary,
  DeliveryStopContact,
  DeliveryStopInput,
  LalamoveQuotationResult,
} from '../types/delivery';

const TABLE = 'delivery_arrangements';

export type ResolvedCrmClient = {
  id: string;
  company_name: string;
  code: string;
};

function rowToArrangement(row: Record<string, unknown>): DeliveryArrangement {
  const pickup = (row.pickup_json ?? {}) as Record<string, unknown>;
  const dropoff = (row.dropoff_json ?? {}) as Record<string, unknown>;
  return {
    id: String(row.id),
    fulfillment_type: row.fulfillment_type as DeliveryArrangement['fulfillment_type'],
    client_id: row.client_id != null ? String(row.client_id) : null,
    dem_customer_code: String(row.dem_customer_code ?? ''),
    client_name: String(row.client_name ?? ''),
    client_code: String(row.client_code ?? ''),
    reason: String(row.reason ?? ''),
    document_no: row.document_no != null ? String(row.document_no) : null,
    created_by: String(row.created_by ?? ''),
    project_id: row.project_id != null ? String(row.project_id) : null,
    task_id: row.task_id != null ? String(row.task_id) : null,
    batch_id: row.batch_id != null ? String(row.batch_id) : null,
    stop_sequence: Number(row.stop_sequence ?? 1),
    pickup_json: {
      name: String(pickup.name ?? ''),
      phone: String(pickup.phone ?? ''),
      address: String(pickup.address ?? ''),
      unitNo: pickup.unitNo != null ? String(pickup.unitNo) : undefined,
      building: pickup.building != null ? String(pickup.building) : undefined,
      postal: pickup.postal != null ? String(pickup.postal) : undefined,
      notesToDriver: pickup.notesToDriver != null ? String(pickup.notesToDriver) : undefined,
    },
    dropoff_json: {
      name: String(dropoff.name ?? ''),
      phone: String(dropoff.phone ?? ''),
      address: String(dropoff.address ?? ''),
      unitNo: dropoff.unitNo != null ? String(dropoff.unitNo) : undefined,
      building: dropoff.building != null ? String(dropoff.building) : undefined,
      postal: dropoff.postal != null ? String(dropoff.postal) : undefined,
      notesToDriver: dropoff.notesToDriver != null ? String(dropoff.notesToDriver) : undefined,
    },
    vehicle_type: row.vehicle_type != null ? String(row.vehicle_type) : null,
    lalamove_quotation_id: String(row.lalamove_quotation_id ?? ''),
    quotation_expires_at: row.quotation_expires_at != null ? String(row.quotation_expires_at) : null,
    quoted_at: String(row.quoted_at ?? ''),
    reference_fee_amount: Number(row.reference_fee_amount ?? 0),
    reference_fee_currency: String(row.reference_fee_currency ?? 'SGD'),
    fee_breakdown_json: row.fee_breakdown_json ?? null,
    raw_quotation_json: row.raw_quotation_json ?? null,
    lalamove_order_id: row.lalamove_order_id != null ? String(row.lalamove_order_id) : null,
    status: String(row.status ?? ''),
    tracking_url: row.tracking_url != null ? String(row.tracking_url) : null,
    fee_amount: row.fee_amount != null ? Number(row.fee_amount) : null,
    internal_assignee_type:
      row.internal_assignee_type as DeliveryArrangement['internal_assignee_type'],
    internal_assignee_name:
      row.internal_assignee_name != null ? String(row.internal_assignee_name) : null,
    internal_notes: row.internal_notes != null ? String(row.internal_notes) : null,
    requested_by: row.requested_by != null ? String(row.requested_by) : null,
    issue_caused_by: row.issue_caused_by != null ? String(row.issue_caused_by) : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

async function readFunctionsHttpError(error: FunctionsHttpError): Promise<string> {
  try {
    const body = await error.context.json();
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      const row = body as Record<string, unknown>;
      if (typeof row.error === 'string' && row.error.trim()) return row.error;
      if (typeof row.message === 'string' && row.message.trim()) return row.message;
      return JSON.stringify(body);
    }
  } catch {
    // fall through
  }
  return error.message || 'Edge Function returned a non-2xx status code';
}

async function invokeLalamove<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await getDbSupabase('dem').functions.invoke('lalamove-api', { body });
  if (error) {
    if (error instanceof FunctionsHttpError) {
      throw new Error(await readFunctionsHttpError(error));
    }
    throw new Error(formatSupabaseError(error));
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String((data as { error: string }).error));
  }
  return data as T;
}

export async function resolveCrmClientFromDemCode(
  demCode: string,
): Promise<ResolvedCrmClient | null> {
  const mainCode = demMainCustomerCode(demCode);
  if (!mainCode) return null;

  const { data, error } = await getDbSupabase('crm')
    .from('clients')
    .select('id, company_name, code')
    .ilike('code', mainCode)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;

  const row = data as Record<string, unknown>;
  return {
    id: String(row.id),
    company_name: String(row.company_name ?? ''),
    code: String(row.code ?? mainCode),
  };
}

export function crmMatchWarning(mainCode: string): string {
  return `No matching CRM account for code ${mainCode}. This delivery is saved in DEM only until a CRM account exists.`;
}

export function isCrmMatched(row: DeliveryArrangement): boolean {
  return row.client_id != null && row.client_id !== '';
}

async function tryLinkCrmClient(row: DeliveryArrangement): Promise<DeliveryArrangement> {
  if (isCrmMatched(row) || !row.dem_customer_code.trim()) return row;

  const crm = await resolveCrmClientFromDemCode(row.dem_customer_code);
  if (!crm) return row;

  const { data, error } = await getDbSupabase('dem')
    .from(TABLE)
    .update({
      client_id: crm.id,
      client_name: crm.company_name,
      client_code: crm.code,
      updated_at: new Date().toISOString(),
    })
    .eq('id', row.id)
    .select('*')
    .single();

  if (error) throw new Error(formatSupabaseError(error));
  return rowToArrangement(data as Record<string, unknown>);
}

export async function fetchDeliveryArrangements(): Promise<DeliveryArrangement[]> {
  const { data, error } = await getDbSupabase('dem')
    .from(TABLE)
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => rowToArrangement(row as Record<string, unknown>));
}

export async function fetchDeliveryArrangement(id: string): Promise<DeliveryArrangement | null> {
  const { data, error } = await getDbSupabase('dem')
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;
  return tryLinkCrmClient(rowToArrangement(data as Record<string, unknown>));
}

export async function fetchDeliveryBatch(id: string): Promise<DeliveryArrangement[]> {
  const row = await fetchDeliveryArrangement(id);
  if (!row) return [];

  if (row.batch_id) {
    const { data, error } = await getDbSupabase('dem')
      .from(TABLE)
      .select('*')
      .eq('batch_id', row.batch_id)
      .order('stop_sequence', { ascending: true });
    if (error) throw new Error(formatSupabaseError(error));
    const rows = (data ?? []).map((item) => rowToArrangement(item as Record<string, unknown>));
    const linked = await Promise.all(rows.map((item) => tryLinkCrmClient(item)));
    if (linked.length > 0) return linked;
  }

  // Fallback for older rows that share a Lalamove order but lost/missing batch_id.
  const orderId = row.lalamove_order_id?.trim();
  if (orderId) {
    const { data, error } = await getDbSupabase('dem')
      .from(TABLE)
      .select('*')
      .eq('lalamove_order_id', orderId)
      .order('stop_sequence', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw new Error(formatSupabaseError(error));
    const rows = (data ?? []).map((item) => rowToArrangement(item as Record<string, unknown>));
    if (rows.length > 1) {
      const linked = await Promise.all(rows.map((item) => tryLinkCrmClient(item)));
      return linked;
    }
  }

  return [row];
}

export async function fetchDeliveryArrangementsForClient(
  clientId: string,
): Promise<DeliveryArrangement[]> {
  const { data, error } = await getDbSupabase('dem')
    .from(TABLE)
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => rowToArrangement(row as Record<string, unknown>));
}

export function summarizeDeliveryArrangements(
  rows: DeliveryArrangement[],
): DeliveryArrangementSummary {
  const year = new Date().getFullYear();
  let lalamoveCount = 0;
  let internalCount = 0;
  let thisYear = 0;
  let referenceFeeTotal = 0;

  for (const row of rows) {
    if (row.fulfillment_type === 'lalamove') lalamoveCount += 1;
    else internalCount += 1;
    if (row.stop_sequence <= 1) {
      referenceFeeTotal += row.reference_fee_amount ?? 0;
    }
    const y = new Date(row.created_at).getFullYear();
    if (y === year) thisYear += 1;
  }

  return {
    total: rows.length,
    lalamoveCount,
    internalCount,
    thisYear,
    referenceFeeTotal,
  };
}

export type DeliveryBatchAccount = {
  name: string;
  code: string;
  reason: string;
};

/** Group multi-stop rows by batch; heads are stop_sequence <= 1 (or sole rows). */
export function groupDeliveryArrangementBatches(
  rows: DeliveryArrangement[],
): Array<{
  head: DeliveryArrangement;
  stops: DeliveryArrangement[];
  accounts: DeliveryBatchAccount[];
}> {
  const byBatch = new Map<string, DeliveryArrangement[]>();
  for (const row of rows) {
    const key =
      row.batch_id?.trim() ||
      (row.fulfillment_type === 'lalamove' ? row.lalamove_order_id?.trim() : '') ||
      row.id;
    const list = byBatch.get(key);
    if (list) list.push(row);
    else byBatch.set(key, [row]);
  }

  const groups: Array<{
    head: DeliveryArrangement;
    stops: DeliveryArrangement[];
    accounts: DeliveryBatchAccount[];
  }> = [];

  for (const stops of byBatch.values()) {
    const sorted = [...stops].sort((a, b) => {
      const seq = a.stop_sequence - b.stop_sequence;
      if (seq !== 0) return seq;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
    const head = sorted.find((row) => row.stop_sequence <= 1) ?? sorted[0]!;
    const accounts = sorted.map((stop) => ({
      name: stop.client_name.trim() || stop.dem_customer_code.trim() || '—',
      code: stop.dem_customer_code.trim(),
      reason: stop.reason.trim(),
    }));
    groups.push({ head, stops: sorted, accounts });
  }

  groups.sort(
    (a, b) => new Date(b.head.created_at).getTime() - new Date(a.head.created_at).getTime(),
  );
  return groups;
}

export function formatDeliveryBatchAccountsLabel(accounts: DeliveryBatchAccount[]): string {
  if (accounts.length === 0) return '—';
  if (accounts.length === 1) {
    const only = accounts[0]!;
    return only.code && only.code !== only.name ? `${only.name} (${only.code})` : only.name;
  }
  const labels = accounts.map((account) => {
    if (account.code && account.name && account.code !== account.name) {
      return `${account.name} (${account.code})`;
    }
    return account.name || account.code || '—';
  });
  if (labels.length <= 2) return `${labels.join(' · ')} · ${accounts.length} stops`;
  return `${labels[0]} · ${labels[1]} +${accounts.length - 2} · ${accounts.length} stops`;
}

type GeoStop = DeliveryStopContact & { lat: number; lng: number };

export type DeliveryGeocodedStops = {
  pickup: GeoStop;
  dropoffs: GeoStop[];
};

export type DeliveryQuotationPreview = LalamoveQuotationResult & {
  stops: Array<{ stopId?: string; stop_id?: string; id?: string }>;
};

/** Normalize local/SG numbers to Lalamove E.164 (+65xxxxxxxx). */
export function normalizeSingaporePhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (/^\+[1-9]\d{1,14}$/.test(trimmed)) return trimmed;

  const digits = trimmed.replace(/\D/g, '');
  if (digits.startsWith('65') && digits.length === 10) return `+${digits}`;
  if (digits.length === 8) return `+65${digits}`;
  if (digits.length === 9 && digits.startsWith('0')) return `+65${digits.slice(1)}`;

  return null;
}

export function formatLalamovePhoneError(label: string): string {
  return `${label} must be a valid phone number.`;
}

function friendlyLalamoveError(message: string): string {
  if (message === 'ERR_INSUFFICIENT_CREDIT' || /insufficient credit/i.test(message)) {
    return 'Lalamove wallet has insufficient credit. Top up your Sandbox wallet in Partner Portal.';
  }
  if (/an error has occurred/i.test(message) && /request id/i.test(message)) {
    return `${message} This is usually caused by an invalid quote reference, duplicate pickup/dropoff phone numbers, or insufficient Sandbox wallet balance.`;
  }
  return message;
}

function assertLalamoveContact(name: string, phone: string, label: string): string {
  const trimmedName = name.trim();
  const normalizedPhone = normalizeDeliveryPhone(phone);
  if (!trimmedName) {
    throw new Error(`${label} name is required for Lalamove delivery.`);
  }
  if (!normalizedPhone) {
    throw new Error(formatLalamovePhoneError(`${label} phone`));
  }
  return normalizedPhone;
}

export async function fetchMultiStopDeliveryQuote(input: {
  pickup: GeoStop;
  dropoffs: GeoStop[];
  vehicleType: string;
  specialRequests?: string[];
}): Promise<DeliveryQuotationPreview> {
  const quotation = await requestLalamoveQuotationMulti(input);
  if (quotation.expiresAt && new Date(quotation.expiresAt).getTime() < Date.now()) {
    throw new Error('Quote expired. Please get a new quote.');
  }
  return quotation;
}

export function isDeliveryQuotationExpired(quotation: DeliveryQuotationPreview | null): boolean {
  if (!quotation?.expiresAt) return false;
  return new Date(quotation.expiresAt).getTime() < Date.now();
}

async function requestLalamoveQuotationMulti(input: {
  pickup: GeoStop;
  dropoffs: GeoStop[];
  vehicleType: string;
  specialRequests?: string[];
}): Promise<DeliveryQuotationPreview> {
  const specialRequests = (input.specialRequests ?? []).map((item) => item.trim()).filter(Boolean);
  return invokeLalamove({
    action: 'createQuotation',
    vehicleType: input.vehicleType,
    ...(specialRequests.length > 0 ? { specialRequests } : {}),
    stops: [
      {
        coordinates: { lat: input.pickup.lat, lng: input.pickup.lng },
        address: input.pickup.address,
      },
      ...input.dropoffs.map((stop) => ({
        coordinates: { lat: stop.lat, lng: stop.lng },
        address: stop.address,
      })),
    ],
  });
}

export type LalamoveApiStatus = {
  connected: boolean;
  balance: number | null;
  serviceCount: number;
  sandbox: boolean;
  message?: string;
  error?: string;
};

export async function fetchLalamoveWalletBalance(): Promise<number | null> {
  const status = await fetchLalamoveApiStatus();
  return status.balance;
}

export async function fetchLalamoveApiStatus(): Promise<LalamoveApiStatus> {
  try {
    const result = await invokeLalamove<{
      balance: number | null;
      connected?: boolean;
      serviceCount?: number;
      sandbox?: boolean;
      message?: string;
    }>({ action: 'getWalletBalance' });
    return {
      connected: result.connected ?? true,
      balance: result.balance ?? null,
      serviceCount: result.serviceCount ?? 0,
      sandbox: result.sandbox ?? true,
      message: result.message,
    };
  } catch (err) {
    return {
      connected: false,
      balance: null,
      serviceCount: 0,
      sandbox: true,
      error: err instanceof Error ? err.message : 'Lalamove API unavailable',
    };
  }
}

export type LalamoveSpecialRequestOption = {
  name: string;
  description: string;
  parentType?: string;
  maxSelection?: number;
};

export type LalamoveVehicleOption = {
  value: string;
  label: string;
  /** Lalamove marketing / suitability text */
  description?: string;
  /** Size and load summary for UI hints */
  detail?: string;
  specialRequests?: LalamoveSpecialRequestOption[];
};

/** Fallback when Lalamove cities API is unavailable — common SG service keys. */
export const LALAMOVE_SG_VEHICLE_FALLBACK: LalamoveVehicleOption[] = [
  { value: 'MOTORCYCLE', label: 'Motorcycle', detail: 'Small parcels · up to about 8 kg' },
  { value: 'CAR', label: 'Car', detail: 'Medium items · up to about 20 kg' },
  { value: 'MPV', label: 'MPV', detail: 'Medium-large items · up to about 50 kg' },
  { value: 'MINIVAN', label: '1.7m Van', detail: 'About 1.6 × 1.2 × 1.0 m · up to about 400 kg' },
  { value: 'VAN', label: '2.4m Van', detail: 'About 2.3 × 1.2 × 1.2 m · up to about 800 kg' },
  { value: 'TRUCK330', label: '10ft Lorry', detail: 'About 2.9 × 1.4 × 1.7 m · up to about 1200 kg' },
  { value: 'TRUCK550', label: '14ft Lorry', detail: 'About 4.2 × 1.7 × 1.9 m · up to about 2000 kg' },
  { value: 'LORRY_24FT', label: '24ft Lorry', detail: 'About 7.5 × 2.4 × 2.3 m · up to about 5000 kg' },
];

const LALAMOVE_VEHICLE_SIZE_RANK: Record<string, number> = {
  MOTORCYCLE: 10,
  COURIER: 10,
  CAR: 20,
  MPV: 30,
  MINIVAN: 40,
  VAN17: 40,
  VAN_17: 40,
  VAN: 50,
  VAN24: 50,
  VAN_24: 50,
  TRUCK330: 60,
  LORRY10: 60,
  TRUCK550: 70,
  LORRY14: 70,
  LORRY_24FT: 80,
  LORRY24: 80,
  TRUCK: 80,
};

export function isLalamoveTowTruckService(value: string): boolean {
  return value.trim().toUpperCase().includes('TOW');
}

function lalamoveVehicleSizeRank(value: string): number {
  const key = value.trim().toUpperCase();
  if (isLalamoveTowTruckService(key)) return -1;
  const known = LALAMOVE_VEHICLE_SIZE_RANK[key];
  if (known != null) return known;

  const truckMatch = /^TRUCK(\d+)$/.exec(key);
  if (truckMatch) {
    const cm = Number(truckMatch[1]);
    if (cm >= 700) return 80;
    if (cm >= 400) return 70;
    if (cm >= 270) return 60;
    return 65;
  }
  if (key.includes('LORRY')) return 75;
  if (key.includes('VAN') || key === 'MINIVAN') return 45;
  return 900;
}

/** Smallest → largest; excludes tow trucks. */
export function sortLalamoveVehicleOptions(options: LalamoveVehicleOption[]): LalamoveVehicleOption[] {
  return [...options]
    .filter((opt) => !isLalamoveTowTruckService(opt.value))
    .sort((a, b) => {
      const rankDiff = lalamoveVehicleSizeRank(a.value) - lalamoveVehicleSizeRank(b.value);
      if (rankDiff !== 0) return rankDiff;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
}

/** Friendly SG names for Lalamove serviceType keys (website names ≠ API keys). */
export function friendlyLalamoveVehicleName(key: string): string {
  const known: Record<string, string> = {
    MOTORCYCLE: 'Motorcycle',
    COURIER: 'Motorcycle / Courier',
    CAR: 'Car',
    MINIVAN: '1.7m Van',
    MPV: 'MPV',
    VAN: '2.4m Van',
    VAN17: '1.7m Van',
    VAN24: '2.4m Van',
    VAN_17: '1.7m Van',
    VAN_24: '2.4m Van',
    LORRY10: '10ft Lorry',
    LORRY14: '14ft Lorry',
    LORRY24: '24ft Lorry',
    LORRY_24FT: '24ft Lorry',
    TRUCK: 'Lorry',
    TRUCK330: '10ft Lorry',
    TRUCK550: '14ft Lorry',
    TOW_TRUCK: 'Tow truck',
    TOW_TRUCK_CAR: 'Car tow truck',
  };
  const upper = key.trim().toUpperCase();
  if (known[upper]) return known[upper];
  const truckMatch = /^TRUCK(\d+)$/.exec(upper);
  if (truckMatch) {
    const cm = Number(truckMatch[1]);
    if (cm >= 700) return '24ft Lorry';
    if (cm >= 400) return '14ft Lorry';
    if (cm >= 270) return '10ft Lorry';
  }
  return upper
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export async function fetchLalamoveServiceTypes(): Promise<LalamoveVehicleOption[]> {
  try {
    const result = await invokeLalamove<{ services: LalamoveVehicleOption[] }>({
      action: 'getServiceTypes',
    });
    const seen = new Set<string>();
    const services = (result.services ?? [])
      .filter((item) => item.value)
      .map((item) => {
        const value = item.value.trim();
        return {
          value,
          // Always show human names — never raw API keys in the dropdown.
          label: friendlyLalamoveVehicleName(value),
          description: item.description?.trim() || undefined,
          detail: item.detail?.trim() || undefined,
          specialRequests: item.specialRequests ?? [],
        };
      })
      .filter((item) => {
        if (seen.has(item.value)) return false;
        seen.add(item.value);
        return true;
      });
    const sorted = sortLalamoveVehicleOptions(services);
    return sorted.length > 0 ? sorted : LALAMOVE_SG_VEHICLE_FALLBACK;
  } catch {
    return LALAMOVE_SG_VEHICLE_FALLBACK;
  }
}

function quotationPayload(quotation: LalamoveQuotationResult, includeFee: boolean) {
  return {
    lalamove_quotation_id: quotation.quotationId,
    quotation_expires_at: quotation.expiresAt,
    quoted_at: new Date().toISOString(),
    reference_fee_amount: includeFee ? quotation.referenceFeeAmount : 0,
    reference_fee_currency: quotation.referenceFeeCurrency,
    fee_breakdown_json: includeFee ? quotation.feeBreakdown : null,
    raw_quotation_json: quotation.raw,
    vehicle_type: quotation.vehicleType,
  };
}

function stopIdAt(stops: Array<{ stopId?: string; stop_id?: string; id?: string }>, index: number): string {
  const stop = stops[index];
  const id = stop?.stopId ?? stop?.stop_id ?? stop?.id;
  if (!id) {
    throw new Error('Quotation stops missing stop IDs. Please try again.');
  }
  return String(id);
}

export async function confirmMultiStopDelivery(
  input: ConfirmMultiStopDeliveryInput,
  geocoded: DeliveryGeocodedStops,
  options?: { existingQuotation?: DeliveryQuotationPreview },
): Promise<DeliveryArrangement> {
  if (input.stops.length === 0) {
    throw new Error('Add at least one dropoff stop.');
  }

  const quotation =
    options?.existingQuotation ??
    (await fetchMultiStopDeliveryQuote({
      pickup: geocoded.pickup,
      dropoffs: geocoded.dropoffs,
      vehicleType: input.vehicleType,
      specialRequests: input.specialRequests,
    }));

  if (isDeliveryQuotationExpired(quotation)) {
    throw new Error('Quote expired. Please get a new quote.');
  }

  const batchId = crypto.randomUUID();
  const now = new Date().toISOString();
  let order:
    | { orderId: string; status: string; trackingUrl: string | null; feeAmount: number | null }
    | null = null;

  if (input.fulfillmentType === 'lalamove') {
    const pickupStopId = stopIdAt(quotation.stops, 0);
    const pickupPhone = assertLalamoveContact(input.pickup.name, input.pickup.phone, 'Pickup');
    const dropoffStops = input.stops.map((stop, index) => {
      const phone = assertLalamoveContact(
        stop.dropoff.name,
        stop.dropoff.phone,
        `Dropoff ${index + 1}`,
      );
      if (phone === pickupPhone) {
        throw new Error(`Dropoff ${index + 1} phone must be different from pickup phone.`);
      }
      // Reason / document no. stay internal — only notes to drivers go to Lalamove remarks.
      const remarks = (stop.notesToDriver ?? stop.dropoff.notesToDriver ?? '').trim();
      return {
        stopId: stopIdAt(quotation.stops, index + 1),
        name: stop.dropoff.name.trim(),
        phone,
        ...(remarks ? { remarks } : {}),
      };
    });

    try {
      order = await invokeLalamove({
        action: 'placeOrder',
        quotationId: quotation.quotationId,
        pickup: {
          stopId: pickupStopId,
          name: input.pickup.name.trim(),
          phone: pickupPhone,
        },
        dropoffs: dropoffStops,
        metadata: {
          batchId: String(batchId),
          stopCount: String(input.stops.length),
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(friendlyLalamoveError(message));
    }
  }

  const rows = input.stops.map((stop, index) => ({
    fulfillment_type: input.fulfillmentType,
    client_id: stop.clientId || null,
    dem_customer_code: stop.demCustomerCode,
    client_name: stop.clientName,
    client_code: stop.clientCode,
    reason: stop.reason.trim(),
    document_no: stop.documentNo?.trim() || null,
    created_by: input.createdBy,
    created_by_auth_id: input.createdByAuthId?.trim() || null,
    // Always stamp a batch id for multi-stop so list/detail can regroup reliably.
    batch_id: input.stops.length > 1 ? batchId : null,
    stop_sequence: index + 1,
    pickup_json: input.pickup,
    dropoff_json: {
      ...stop.dropoff,
      notesToDriver: stop.notesToDriver?.trim() || stop.dropoff.notesToDriver?.trim() || undefined,
    },
    status:
      input.fulfillmentType === 'lalamove'
        ? order?.status ?? 'UNKNOWN'
        : 'scheduled',
    lalamove_order_id: order?.orderId ?? null,
    tracking_url: order?.trackingUrl ?? null,
    fee_amount:
      input.fulfillmentType === 'lalamove' && index === 0
        ? order?.feeAmount ?? quotation.referenceFeeAmount
        : null,
    internal_assignee_type:
      input.fulfillmentType === 'internal' ? input.internalAssigneeType ?? null : null,
    internal_assignee_name:
      input.fulfillmentType === 'internal' ? input.internalAssigneeName?.trim() || null : null,
    internal_notes: input.internalNotes?.trim() || null,
    requested_by: input.requestedBy?.trim() || null,
    issue_caused_by: input.issueCausedBy?.trim() || null,
    ...quotationPayload(quotation, index === 0),
    updated_at: now,
  }));

  const { data, error } = await getDbSupabase('dem').from(TABLE).insert(rows).select('*');
  if (error) throw new Error(formatSupabaseError(error));

  const created = (data ?? []).map((row) => rowToArrangement(row as Record<string, unknown>));
  if (created.length !== input.stops.length) {
    throw new Error(
      `Saved ${created.length} of ${input.stops.length} stops. Please refresh Deliveries and check this order.`,
    );
  }
  return created.sort((a, b) => a.stop_sequence - b.stop_sequence)[0]!;
}

function normalizeContactForCompare(contact: DeliveryStopContact): string {
  return JSON.stringify({
    name: contact.name?.trim() ?? '',
    phone: contact.phone?.trim() ?? '',
    address: contact.address?.trim() ?? '',
    unitNo: contact.unitNo?.trim() ?? '',
    building: contact.building?.trim() ?? '',
    postal: contact.postal?.trim() ?? '',
    notesToDriver: contact.notesToDriver?.trim() ?? '',
  });
}

function stopContentChanged(
  original: DeliveryArrangement,
  stop: DeliveryStopInput,
  pickup: DeliveryStopContact,
): boolean {
  const dropoff: DeliveryStopContact = {
    ...stop.dropoff,
    notesToDriver: stop.notesToDriver?.trim() || stop.dropoff.notesToDriver?.trim() || undefined,
  };

  if (normalizeContactForCompare(original.pickup_json) !== normalizeContactForCompare(pickup)) {
    return true;
  }
  if (normalizeContactForCompare(original.dropoff_json) !== normalizeContactForCompare(dropoff)) {
    return true;
  }
  if (original.dem_customer_code.trim() !== stop.demCustomerCode.trim()) return true;
  if ((original.client_id ?? null) !== (stop.clientId ?? null)) return true;
  if (original.client_name.trim() !== stop.clientName.trim()) return true;
  if (original.client_code.trim() !== stop.clientCode.trim()) return true;
  if (original.reason.trim() !== stop.reason.trim()) return true;
  if ((original.document_no ?? '').trim() !== (stop.documentNo?.trim() ?? '')) return true;
  return false;
}

function internalAssigneeChanged(
  original: DeliveryArrangement,
  input: ConfirmMultiStopDeliveryInput,
): boolean {
  if (input.fulfillmentType !== 'internal') return false;
  const nextType = input.internalAssigneeType ?? null;
  const nextName = input.internalAssigneeName?.trim() || null;
  return (
    original.internal_assignee_type !== nextType ||
    (original.internal_assignee_name?.trim() || null) !== nextName
  );
}

/** Re-place a cancelled delivery on the same arrangement row(s) — no new list entry. */
export async function reorderCancelledDelivery(
  arrangementId: string,
  input: ConfirmMultiStopDeliveryInput,
  geocoded: DeliveryGeocodedStops,
  options?: { existingQuotation?: DeliveryQuotationPreview },
): Promise<DeliveryArrangement> {
  if (input.stops.length === 0) {
    throw new Error('Add at least one dropoff stop.');
  }

  const originalBatch = await fetchDeliveryBatch(arrangementId);
  if (originalBatch.length === 0) {
    throw new Error('Delivery not found.');
  }
  if (!originalBatch.every((row) => isDeliveryStatusCancelled(row.status))) {
    throw new Error('Only cancelled deliveries can be reordered.');
  }
  if (originalBatch[0]!.fulfillment_type !== input.fulfillmentType) {
    throw new Error('Fulfillment type cannot change when reordering.');
  }

  const quotation =
    options?.existingQuotation ??
    (await fetchMultiStopDeliveryQuote({
      pickup: geocoded.pickup,
      dropoffs: geocoded.dropoffs,
      vehicleType: input.vehicleType,
      specialRequests: input.specialRequests,
    }));

  if (input.fulfillmentType === 'lalamove' && isDeliveryQuotationExpired(quotation)) {
    throw new Error('Quote expired. Please get a new quote.');
  }

  const head = originalBatch[0]!;
  const now = new Date().toISOString();
  const multiStop = input.stops.length > 1;
  let batchId = head.batch_id;
  if (multiStop && !batchId) batchId = crypto.randomUUID();
  if (!multiStop) batchId = null;

  let order:
    | { orderId: string; status: string; trackingUrl: string | null; feeAmount: number | null }
    | null = null;

  if (input.fulfillmentType === 'lalamove' && quotation) {
    const pickupStopId = stopIdAt(quotation.stops, 0);
    const pickupPhone = assertLalamoveContact(input.pickup.name, input.pickup.phone, 'Pickup');
    const dropoffStops = input.stops.map((stop, index) => {
      const phone = assertLalamoveContact(
        stop.dropoff.name,
        stop.dropoff.phone,
        `Dropoff ${index + 1}`,
      );
      if (phone === pickupPhone) {
        throw new Error(`Dropoff ${index + 1} phone must be different from pickup phone.`);
      }
      const remarks = (stop.notesToDriver ?? stop.dropoff.notesToDriver ?? '').trim();
      return {
        stopId: stopIdAt(quotation.stops, index + 1),
        name: stop.dropoff.name.trim(),
        phone,
        ...(remarks ? { remarks } : {}),
      };
    });

    try {
      order = await invokeLalamove({
        action: 'placeOrder',
        quotationId: quotation.quotationId,
        pickup: {
          stopId: pickupStopId,
          name: input.pickup.name.trim(),
          phone: pickupPhone,
        },
        dropoffs: dropoffStops,
        metadata: {
          batchId: String(batchId ?? head.id),
          stopCount: String(input.stops.length),
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(friendlyLalamoveError(message));
    }
  }

  const vehicleChanged = (head.vehicle_type?.trim() || '') !== input.vehicleType.trim();
  const assigneeChanged = internalAssigneeChanged(head, input);

  for (let index = 0; index < input.stops.length; index += 1) {
    const stop = input.stops[index]!;
    const original = originalBatch[index];
    const dropoffJson: DeliveryStopContact = {
      ...stop.dropoff,
      notesToDriver: stop.notesToDriver?.trim() || stop.dropoff.notesToDriver?.trim() || undefined,
    };

    const alwaysUpdate: Record<string, unknown> = {
      created_at: now,
      created_by: input.createdBy,
      created_by_auth_id: input.createdByAuthId?.trim() || null,
      updated_at: now,
      stop_sequence: index + 1,
      batch_id: batchId,
      status:
        input.fulfillmentType === 'lalamove'
          ? order?.status ?? 'UNKNOWN'
          : 'scheduled',
      lalamove_order_id: order?.orderId ?? null,
      tracking_url: order?.trackingUrl ?? null,
      fee_amount:
        input.fulfillmentType === 'lalamove' && index === 0
          ? order?.feeAmount ?? quotation.referenceFeeAmount
          : null,
      ...quotationPayload(quotation, index === 0),
      requested_by: input.requestedBy?.trim() || null,
      issue_caused_by: input.issueCausedBy?.trim() || null,
    };

    const contentUpdate: Record<string, unknown> = {};
    const contentChanged =
      !original ||
      stopContentChanged(original, stop, input.pickup) ||
      vehicleChanged ||
      assigneeChanged;

    if (contentChanged) {
      Object.assign(contentUpdate, {
        fulfillment_type: input.fulfillmentType,
        client_id: stop.clientId,
        dem_customer_code: stop.demCustomerCode,
        client_name: stop.clientName,
        client_code: stop.clientCode,
        reason: stop.reason.trim(),
        document_no: stop.documentNo?.trim() || null,
        pickup_json: input.pickup,
        dropoff_json: dropoffJson,
        vehicle_type: input.vehicleType,
        internal_assignee_type:
          input.fulfillmentType === 'internal' ? input.internalAssigneeType ?? null : null,
        internal_assignee_name:
          input.fulfillmentType === 'internal' ? input.internalAssigneeName?.trim() || null : null,
        internal_notes: input.internalNotes?.trim() || null,
        requested_by: input.requestedBy?.trim() || null,
        issue_caused_by: input.issueCausedBy?.trim() || null,
      });
    }

    if (original) {
      const { error } = await getDbSupabase('dem')
        .from(TABLE)
        .update({ ...alwaysUpdate, ...contentUpdate })
        .eq('id', original.id);
      if (error) throw new Error(formatSupabaseError(error));
    } else {
      const { error } = await getDbSupabase('dem').from(TABLE).insert({
        ...alwaysUpdate,
        fulfillment_type: input.fulfillmentType,
        client_id: stop.clientId,
        dem_customer_code: stop.demCustomerCode,
        client_name: stop.clientName,
        client_code: stop.clientCode,
        reason: stop.reason.trim(),
        document_no: stop.documentNo?.trim() || null,
        pickup_json: input.pickup,
        dropoff_json: dropoffJson,
        vehicle_type: input.vehicleType,
        internal_assignee_type:
          input.fulfillmentType === 'internal' ? input.internalAssigneeType ?? null : null,
        internal_assignee_name:
          input.fulfillmentType === 'internal' ? input.internalAssigneeName?.trim() || null : null,
        internal_notes: input.internalNotes?.trim() || null,
        requested_by: input.requestedBy?.trim() || null,
        issue_caused_by: input.issueCausedBy?.trim() || null,
      });
      if (error) throw new Error(formatSupabaseError(error));
    }
  }

  for (const extra of originalBatch.slice(input.stops.length)) {
    const { error } = await getDbSupabase('dem').from(TABLE).delete().eq('id', extra.id);
    if (error) throw new Error(formatSupabaseError(error));
  }

  const refreshed = await fetchDeliveryArrangement(head.id);
  if (!refreshed) throw new Error('Delivery not found after reorder.');
  return refreshed;
}

export async function refreshLalamoveDeliveryStatus(id: string): Promise<DeliveryArrangement> {
  const existing = await fetchDeliveryArrangement(id);
  if (!existing?.lalamove_order_id) {
    throw new Error('Not a Lalamove order.');
  }

  const order = await invokeLalamove<{
    orderId: string;
    status: string;
    trackingUrl: string | null;
    feeAmount: number | null;
  }>({
    action: 'retrieveOrder',
    orderId: existing.lalamove_order_id,
  });

  const updatePayload = {
    status: normalizeDeliveryStatus(order.status) || order.status,
    tracking_url: order.trackingUrl,
    fee_amount: order.feeAmount ?? existing.fee_amount,
    updated_at: new Date().toISOString(),
  };

  if (existing.batch_id) {
    const { error: batchError } = await getDbSupabase('dem')
      .from(TABLE)
      .update(updatePayload)
      .eq('lalamove_order_id', existing.lalamove_order_id);
    if (batchError) throw new Error(formatSupabaseError(batchError));
  } else {
    const { error: singleError } = await getDbSupabase('dem')
      .from(TABLE)
      .update(updatePayload)
      .eq('id', id);
    if (singleError) throw new Error(formatSupabaseError(singleError));
  }

  const refreshed = await fetchDeliveryArrangement(id);
  if (!refreshed) throw new Error('Delivery not found after refresh.');
  return refreshed;
}

/** Refresh non-terminal Lalamove rows from the API so list status matches Lalamove. */
export async function syncStaleLalamoveDeliveryStatuses(
  rows: DeliveryArrangement[],
): Promise<DeliveryArrangement[]> {
  const seen = new Set<string>();
  const toRefresh: DeliveryArrangement[] = [];

  for (const row of rows) {
    if (row.fulfillment_type !== 'lalamove' || !row.lalamove_order_id) continue;
    if (isDeliveryStatusTerminal(row.status)) continue;
    const key = row.lalamove_order_id;
    if (seen.has(key)) continue;
    seen.add(key);
    toRefresh.push(row);
  }

  if (toRefresh.length === 0) return rows;

  const updates = new Map<string, Pick<DeliveryArrangement, 'status' | 'tracking_url' | 'fee_amount' | 'updated_at'>>();
  await Promise.all(
    toRefresh.map(async (row) => {
      try {
        const refreshed = await refreshLalamoveDeliveryStatus(row.id);
        updates.set(row.lalamove_order_id!, {
          status: refreshed.status,
          tracking_url: refreshed.tracking_url,
          fee_amount: refreshed.fee_amount,
          updated_at: refreshed.updated_at,
        });
      } catch {
        // Ignore individual sync failures — list still shows last known DB state.
      }
    }),
  );

  if (updates.size === 0) return rows;

  return rows.map((row) => {
    if (!row.lalamove_order_id) return row;
    const patch = updates.get(row.lalamove_order_id);
    return patch ? { ...row, ...patch } : row;
  });
}

export type LalamoveLiveOrder = {
  orderId: string;
  status: string;
  trackingUrl: string | null;
  feeAmount: number | null;
  driverId: string | null;
  priorityFee: string | null;
  isPODEnabled: boolean;
  stops: Array<{
    stopId: string;
    address: string;
    name: string;
    phone: string;
    lat: number | null;
    lng: number | null;
    pod: { status: string; image: string | null; signedAt: string | null } | null;
  }>;
};

export type LalamoveDriverDetails = {
  driverId: string;
  name: string;
  phone: string;
  plateNumber: string;
  photo: string | null;
  lat: number | null;
  lng: number | null;
  updatedAt: string | null;
};

export async function fetchLalamoveLiveOrder(orderId: string): Promise<LalamoveLiveOrder> {
  return invokeLalamove<LalamoveLiveOrder>({
    action: 'retrieveOrder',
    orderId,
  });
}

export async function fetchLalamoveWebhookInfo(): Promise<{
  suggestedUrl: string;
  sandbox: boolean;
  note?: string;
}> {
  return invokeLalamove({ action: 'getWebhook' });
}

export async function registerLalamoveWebhook(url?: string): Promise<{ url: string; sandbox: boolean }> {
  const target =
    url?.trim() ||
    (await fetchLalamoveWebhookInfo()).suggestedUrl;
  if (!target) throw new Error('Webhook URL is empty.');
  return invokeLalamove({ action: 'setWebhook', url: target });
}

export async function fetchLalamoveDriverDetails(
  orderId: string,
  driverId: string,
): Promise<LalamoveDriverDetails> {
  return invokeLalamove<LalamoveDriverDetails>({
    action: 'getDriver',
    orderId,
    driverId,
  });
}

export async function cancelLalamoveDelivery(id: string): Promise<DeliveryArrangement> {
  const existing = await fetchDeliveryArrangement(id);
  if (!existing?.lalamove_order_id) throw new Error('Not a Lalamove order.');

  await invokeLalamove({ action: 'cancelOrder', orderId: existing.lalamove_order_id });

  const updatePayload = {
    status: 'CANCELED',
    updated_at: new Date().toISOString(),
  };

  if (existing.batch_id) {
    const { error } = await getDbSupabase('dem')
      .from(TABLE)
      .update(updatePayload)
      .eq('lalamove_order_id', existing.lalamove_order_id);
    if (error) throw new Error(formatSupabaseError(error));
  } else {
    const { error } = await getDbSupabase('dem').from(TABLE).update(updatePayload).eq('id', id);
    if (error) throw new Error(formatSupabaseError(error));
  }

  const refreshed = await fetchDeliveryArrangement(id);
  if (!refreshed) throw new Error('Delivery not found after cancel.');
  return refreshed;
}

export async function changeLalamoveDriver(
  id: string,
  reason:
    | 'DRIVER_LATE'
    | 'DRIVER_ASKED_CHANGE'
    | 'DRIVER_UNRESPONSIVE'
    | 'DRIVER_RUDE' = 'DRIVER_UNRESPONSIVE',
): Promise<void> {
  const existing = await fetchDeliveryArrangement(id);
  if (!existing?.lalamove_order_id) throw new Error('Not a Lalamove order.');
  const live = await fetchLalamoveLiveOrder(existing.lalamove_order_id);
  if (!live.driverId) throw new Error('No driver assigned yet.');
  await invokeLalamove({
    action: 'changeDriver',
    orderId: existing.lalamove_order_id,
    driverId: live.driverId,
    reason,
  });
}

export async function addLalamovePriorityFee(
  id: string,
  priorityFee: number,
): Promise<LalamoveLiveOrder> {
  const existing = await fetchDeliveryArrangement(id);
  if (!existing?.lalamove_order_id) throw new Error('Not a Lalamove order.');
  if (!Number.isFinite(priorityFee) || priorityFee <= 0) {
    throw new Error('Enter a positive priority fee amount.');
  }

  // API replaces previous priority fee with this amount (must be greater than previous).
  const tipped = await invokeLalamove<LalamoveLiveOrder>({
    action: 'addPriorityFee',
    orderId: existing.lalamove_order_id,
    priorityFee: String(priorityFee),
  });

  // Prefer tip response; re-fetch if total / priorityFee still missing.
  let live = tipped;
  if (live.feeAmount == null || !live.priorityFee) {
    try {
      const retrieved = await fetchLalamoveLiveOrder(existing.lalamove_order_id);
      live = {
        ...retrieved,
        feeAmount: retrieved.feeAmount ?? tipped.feeAmount,
        priorityFee: retrieved.priorityFee ?? tipped.priorityFee ?? String(priorityFee),
      };
    } catch {
      const base =
        existing.fee_amount ?? existing.reference_fee_amount ?? null;
      live = {
        ...tipped,
        priorityFee: tipped.priorityFee ?? String(priorityFee),
        feeAmount:
          tipped.feeAmount ??
          (base != null ? Number(base) + priorityFee : null),
      };
    }
  }

  // If API still returned base-only total, add the tip we just set.
  if (
    live.feeAmount != null &&
    existing.reference_fee_amount != null &&
    Math.abs(live.feeAmount - Number(existing.reference_fee_amount)) < 0.01 &&
    priorityFee > 0
  ) {
    live = { ...live, feeAmount: Number(existing.reference_fee_amount) + priorityFee };
  }
  if (!live.priorityFee) {
    live = { ...live, priorityFee: String(priorityFee) };
  }

  const updatePayload = {
    status: live.status || existing.status,
    tracking_url: live.trackingUrl ?? existing.tracking_url,
    fee_amount: live.feeAmount ?? existing.fee_amount,
    updated_at: new Date().toISOString(),
  };

  if (existing.batch_id) {
    const { error } = await getDbSupabase('dem')
      .from(TABLE)
      .update(updatePayload)
      .eq('lalamove_order_id', existing.lalamove_order_id);
    if (error) throw new Error(formatSupabaseError(error));
  } else {
    const { error } = await getDbSupabase('dem').from(TABLE).update(updatePayload).eq('id', id);
    if (error) throw new Error(formatSupabaseError(error));
  }

  return live;
}

export async function updateInternalDeliveryStatus(
  id: string,
  status: 'scheduled' | 'completed' | 'cancelled',
): Promise<DeliveryArrangement> {
  const existing = await fetchDeliveryArrangement(id);
  if (!existing) throw new Error('Delivery not found.');

  let query = getDbSupabase('dem')
    .from(TABLE)
    .update({ status, updated_at: new Date().toISOString() })
    .eq('fulfillment_type', 'internal');

  if (existing.batch_id) {
    query = query.eq('batch_id', existing.batch_id);
  } else {
    query = query.eq('id', id);
  }

  const { data, error } = await query.select('*').eq('id', id).single();
  if (error) throw new Error(formatSupabaseError(error));
  return rowToArrangement(data as Record<string, unknown>);
}

export function formatDeliveryMoney(amount: number | null | undefined, currency = 'SGD'): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  if (amount === 0) return '—';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function deliveryTypeLabel(type: DeliveryArrangement['fulfillment_type']): string {
  return type === 'lalamove' ? 'Lalamove' : 'Internal';
}

export function deliveryAssigneeLabel(row: DeliveryArrangement): string {
  if (row.fulfillment_type === 'lalamove') return 'Lalamove';
  if (row.internal_assignee_type === 'driver') return `Driver · ${row.internal_assignee_name ?? '—'}`;
  if (row.internal_assignee_type === 'sales') return `Sales · ${row.internal_assignee_name ?? '—'}`;
  return row.internal_assignee_name ?? '—';
}

/** Normalize Lalamove / internal status for comparisons. */
export function normalizeDeliveryStatus(status: string): string {
  const normalized = status.trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (normalized === 'ONGOING') return 'ON_GOING';
  return normalized;
}

/** Human label without underscores — e.g. ASSIGNING_DRIVER → Assigning driver. */
export function formatDeliveryStatusLabel(status: string): string {
  const raw = status.trim();
  if (!raw) return '—';
  const normalized = normalizeDeliveryStatus(raw);
  const known: Record<string, string> = {
    ASSIGNING_DRIVER: 'Assigning driver',
    ON_GOING: 'On going',
    PICKED_UP: 'Picked up',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    CANCELED: 'Cancelled',
    REJECTED: 'Rejected',
    EXPIRED: 'Expired',
    FAILED: 'Failed',
    SCHEDULED: 'Scheduled',
    PENDING: 'Pending',
  };
  if (known[normalized]) return known[normalized];
  return normalized
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export type DeliveryStatusTone =
  | 'progress'
  | 'active'
  | 'done'
  | 'cancelled'
  | 'warning'
  | 'neutral';

export function deliveryStatusTone(status: string): DeliveryStatusTone {
  const s = normalizeDeliveryStatus(status);
  if (s === 'COMPLETED' || s === 'DONE') return 'done';
  if (s === 'CANCELLED' || s === 'CANCELED' || s === 'REJECTED' || s === 'EXPIRED' || s === 'FAILED') {
    return 'cancelled';
  }
  if (s === 'ASSIGNING_DRIVER' || s === 'PENDING' || s === 'SCHEDULED') return 'progress';
  if (s === 'ON_GOING' || s === 'PICKED_UP') return 'active';
  if (s.includes('FAIL') || s.includes('ERROR')) return 'warning';
  return 'neutral';
}

export function isDeliveryStatusCancelled(status: string): boolean {
  const s = normalizeDeliveryStatus(status);
  return s === 'CANCELLED' || s === 'CANCELED';
}

export function isDeliveryStatusTerminal(status: string): boolean {
  const s = normalizeDeliveryStatus(status);
  return (
    s === 'COMPLETED' ||
    s === 'DONE' ||
    s === 'CANCELLED' ||
    s === 'CANCELED' ||
    s === 'REJECTED' ||
    s === 'EXPIRED' ||
    s === 'FAILED'
  );
}

/** @deprecated Use fetchLalamoveServiceTypes() — kept for backwards compatibility. */
export const LALAMOVE_VEHICLE_OPTIONS = LALAMOVE_SG_VEHICLE_FALLBACK;
